import axios from "axios";
import { 
  GoogleAuthProvider, 
  signInWithPopup, 
  signInWithEmailAndPassword 
} from "firebase/auth";
import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import greenBg from "../assets/green_bg.png";
import FunnelFooterWithLinks from "../components/FunnelFooterWithLinks";
import { useFirebaseAuth } from "../context/AuthContext";
import { auth } from "../services/firebase";
import LogoHeader from "../components/LogoHeader";
import * as Sentry from "@sentry/react";
import { validateEmail, createInputChangeHandler } from "../helpers/formValidationHelper";
import { getBrand } from "../utils/getBrand";

const googleProvider = new GoogleAuthProvider();

const Login = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [formErrors, setFormErrors] = useState({});
  
  const navigate = useNavigate();
  const location = useLocation();
  const { setIdToken } = useFirebaseAuth();
  const { brandSupportEmail } = getBrand();
  // Complete login process (shared by both methods)
  const completeLogin = async (user) => {
    try {
      const idToken = await user.getIdToken();
      
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}funnels/firebase/login`,
        { token: idToken },
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );
      
      if (response.status === 204) {
        setIdToken(idToken);
        // Return the user to the page they were heading to before login
        const from = location.state?.from;
        navigate(from ? `${from.pathname}${from.search || ''}` : "/dashboard");
      } else {
        throw new Error("Unexpected response status");
      }
    } catch (apiError) {
      const status = apiError.response?.status;
      const errorCode = apiError.response?.data?.error;
      const isExplicitReject = status === 401 && errorCode === 'CLIENT_NOT_FOUND';

      if (isExplicitReject) {
        try {
          await user.delete();
        } catch (deleteErr) {
          Sentry.captureException(deleteErr, { extra: { uid: user.uid } });
        }
        Sentry.captureMessage("Backend rejected user login - account deleted", {
          tags: { event_type: 'user_login_blocked' },
          extra: { email: user.email, uid: user.uid, error: apiError.response?.data || apiError.message },
        });
      } else {
        Sentry.captureMessage("Backend login request failed", {
          tags: { event_type: 'login_request_failed' },
          extra: { email: user.email, uid: user.uid, status, error: apiError.response?.data || apiError.message },
        });
      }
      setError(`Login failed. Please check your email and password, or try signing in with Google if that’s how you registered. Need help? Contact us at ${brandSupportEmail}.`);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setLoading(true);
      setError(null);
      await auth.signOut();
      await new Promise(resolve => setTimeout(resolve, 200));
      
      let result;
      try {
        result = await signInWithPopup(auth, googleProvider);
      } catch (error) {
        if (error.code === 'auth/popup-blocked') {
          setError("Please disable popup blocker and try again.");
          Sentry.captureException(error, {
            tags: { error_type: 'popup_blocked' },
            extra: { step: 'login_signin' }
          });
          setLoading(false);
          return;
        }
        if (error.code === 'auth/popup-closed-by-user') {
          setError("Sign-in cancelled. Please try again.");
          setLoading(false);
          return;
        }
        Sentry.captureException(error, {
          tags: { error_type: 'popup_unexpected' },
          extra: { step: 'login_signin' }
        });
        throw error;
      }
      
      await completeLogin(result.user);
      
    } catch (error) {
      Sentry.captureException(error, {
        tags: { error_type: 'login_failed' },
        extra: { step: 'main_login_flow' }
      });
      setError("Authentication failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Email validation using utilities (password validation not needed for login)
  const validateForm = () => {
    const errors = {};
    
    const emailError = validateEmail(formData.email);
    if (emailError) {
      errors.email = emailError;
    }
    
    // Only check if password is provided, not format validation
    if (!formData.password) {
      errors.password = 'Password is required';
    }
    
    return errors;
  };

  // Email/Password Sign-in
  const handleEmailSignIn = async (e) => {
    e.preventDefault();
    
    const errors = validateForm();
    setFormErrors(errors);
    
    if (Object.keys(errors).length > 0) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await auth.signOut();
      await new Promise(resolve => setTimeout(resolve, 200));

      const result = await signInWithEmailAndPassword(
        auth, 
        formData.email, 
        formData.password
      );

      await completeLogin(result.user);
      
    } catch (error) {
      setLoading(false);
      
      // Handle specific Firebase errors
      switch (error.code) {
        case 'auth/user-not-found':
          setError("No account found with this email address.");
          break;
        case 'auth/wrong-password':
          setError("Incorrect password. Please try again.");
          break;
        case 'auth/invalid-email':
          setError("Please enter a valid email address.");
          break;
        case 'auth/too-many-requests':
          setError("Too many failed attempts. Please try again later.");
          break;
        default:
          setError(`Login failed. Please check your email and password, or try signing in with Google if that’s how you registered. Need help? Contact us at ${brandSupportEmail}.`);
      }
      
      Sentry.captureException(error, {
        tags: { error_type: 'email_signin_failed' },
        extra: { email: formData.email }
      });
    }
  };

  // Create input change handlers using utilities
  const handleEmailChange = createInputChangeHandler(
    (value) => setFormData(prev => ({ ...prev, email: value })), 
    setFormErrors, 
    'email'
  );
  
  const handlePasswordChange = createInputChangeHandler(
    (value) => setFormData(prev => ({ ...prev, password: value })), 
    setFormErrors, 
    'password'
  );

  return (
    <>
      <LogoHeader />
      <div className="min-h-screen flex flex-col bg-[#F6F0FA] p-4">
        <div className="flex-1 flex flex-col items-center justify-center py-12">
          <div className="flex flex-col items-center max-w-md w-full">
            <div className="bg-[#C7A4E7] rounded-2xl p-6 mb-6 flex items-center justify-center">
              <svg
                width="48"
                height="48"
                viewBox="0 0 48 48"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M24 24C28.4183 24 32 20.4183 32 16C32 11.5817 28.4183 8 24 8C19.5817 8 16 11.5817 16 16C16 20.4183 19.5817 24 24 24Z"
                  fill="white"
                />
                <path
                  d="M40 40C40 32.268 32.837 26 24 26C15.163 26 8 32.268 8 40"
                  stroke="white"
                  strokeWidth="4"
                  strokeLinecap="round"
                />
              </svg>
            </div>
            <h1 className="text-4xl font-bold text-center mb-4">
              Welcome Back!
            </h1>
            <p className="text-center text-lg text-gray-700 mb-8 max-w-xl">
              Sign in to access your dashboard and continue your job search journey
            </p>

            {!showEmailForm ? (
              // Sign-in options
              <div className="w-full space-y-4">
                <button
                  className="w-full bg-black text-white rounded-full px-8 py-3 font-semibold text-lg flex items-center justify-center"
                  onClick={handleGoogleSignIn}
                  disabled={loading}
                >
                  {loading ? (
                    "Signing in..."
                  ) : (
                    <>
                      <img
                        src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                        alt="Google"
                        className="w-6 h-6 mr-2"
                      />
                      Sign in with Google
                    </>
                  )}
                </button>

                <div className="flex items-center">
                  <hr className="flex-1 border-gray-300" />
                  <span className="px-4 text-gray-500">or</span>
                  <hr className="flex-1 border-gray-300" />
                </div>

                <button
                  className="w-full bg-white border-2 border-black text-black rounded-full px-8 py-3 font-semibold text-lg"
                  onClick={() => setShowEmailForm(true)}
                >
                  Sign in with Email
                </button>
              </div>
            ) : (
              // Email signin form
              <form onSubmit={handleEmailSignIn} className="w-full space-y-4">
                <div>
                  <input
                    type="email"
                    placeholder="Email Address"
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black ${
                      formErrors.email ? 'border-red-500' : 'border-gray-300'
                    }`}
                    value={formData.email}
                    onChange={(e) => handleEmailChange(e.target.value)}
                  />
                  {formErrors.email && (
                    <p className="text-red-600 text-sm mt-1">{formErrors.email}</p>
                  )}
                </div>

                <div>
                  <input
                    type="password"
                    placeholder="Password"
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black ${
                      formErrors.password ? 'border-red-500' : 'border-gray-300'
                    }`}
                    value={formData.password}
                    onChange={(e) => handlePasswordChange(e.target.value)}
                  />
                  {formErrors.password && (
                    <p className="text-red-600 text-sm mt-1">{formErrors.password}</p>
                  )}
                </div>

                <div className="text-right">
                  <button
                    type="button"
                    className="text-sm text-blue-600 hover:text-blue-800 underline"
                    onClick={() => navigate('/reset-password')}
                  >
                    Forgot password?
                  </button>
                </div>

                <button
                  type="submit"
                  className="w-full bg-black text-white rounded-full px-8 py-3 font-semibold text-lg"
                  disabled={loading}
                >
                  {loading ? "Signing in..." : "Sign In"}
                </button>

                <button
                  type="button"
                  className="w-full text-gray-600 text-sm underline"
                  onClick={() => {
                    setShowEmailForm(false);
                    setFormErrors({});
                    setError(null);
                  }}
                >
                  ← Back to sign-in options
                </button>
              </form>
            )}

            {error && (
              <div className="mt-6 p-4 bg-red-50 rounded-lg w-full">
                <p className="text-red-600 text-center">{error}</p>
              </div>
            )}
          </div>
        </div>
      </div>
      <section className="relative p-4">
        <img
          src={greenBg}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <FunnelFooterWithLinks />
      </section>
    </>
  );
};

export default Login;